import {Component} from "react";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import AppCpeCveIcon from "../../media/app-images/AppCpeCveIcon.mjs";
import TextLabel from "../../controls/TextLabel/TextLabel.mjs";
import "./MetricInfoModal.css";
import AppExternalLinkIcon from "../../media/app-images/AppExternalLinkIcon.mjs";
import appLib from "../../libs/appLib.mjs";
import Glob from "../../libs/Glob.mjs";
import CloseIcon from "../../media/images/CloseIcon.mjs";
import AppDownloadIcon from "../../media/app-images/AppDownloadIcon.mjs";

const prefix='MetricInfoModal';

const xpose = entry => {
	let [k,v] = entry.split(':');
	if ( !k || ! v ) return [(k||'*'), null];
	switch (k) {
		case 'CVSS': return [k,null];
		case 'AV':
			k = 'Attack Vector';
			switch (v) {
				case 'N': return [k,'Network','Remotely exploitable',entry];
				case 'A': return [k,'Adjacent','Exploitable via adjacent network (Bluetooth, VPN)',entry];
				case 'L': return [k,'Local','Requires user access (shell, SSH)',entry];
				case 'P': return [k,'Physical','Requires physical interaction with the device',entry];
				default: return [k,null];
			}
		case 'AC' :
			k = 'Attack Complexity';
			switch (v) {
				case 'L': return [k,'Low','Easy to exploit',entry];
				case 'H': return [k,'High','Specialized conditions needed',entry];
				default: return [k,null];
			}
		case 'PR' :
			k = 'Privileges Required';
			switch (v) {
				case 'N': return [k,'None','No access needed',entry];
				case 'L': return [k,'Low','Basic user privileges',entry];
				case 'H': return [k,'High','Administrative privileges',entry];
				default: return [k,null];
			}
		case 'UI':
			k = 'User Interactions';
			switch (v) {
				case 'N': return [k,'None','No user action required',entry];
				case 'R': return [k,'Required','User must perform an action (e.g., click a link)',entry];
				default: return [k,null];
			}
		case 'S':
			k = 'Scope';
			switch (v) {
				case 'U': return [k,'Unchanged','Resource is within the same component',entry];
				case 'C': return [k,'Changed','Vulnerability affects other resources',entry];
				default: return [k,null];
			}
		case 'C':
			k = '[CIA] Confidentiality';
			switch (v) {
				case 'H': case 'C': return [k,'High','Total loss of confidentiality',entry];
				case 'L': case 'P': return [k,'Low','Limited information loss',entry];
				case 'N': return [k,'None','No impact',entry];
				default: return [k,null];
			}
		case 'I':
			k = '[CIA] Integrity';
			switch (v) {
				case 'H': case 'C': return [k,'High','Total data loss or modification',entry];
				case 'L': case 'P': return [k,'Low','Limited modification',entry];
				case 'N': return [k,'None','No impact',entry];
				default: return [k,null];
			}
		case 'A':
			k = '[CIA] Availability';
			switch (v) {
				case 'H': case 'C': return [k,'High','Total loss of availability',entry];
				case 'L': case 'P': return [k,'Low','Limited performance loss',entry];
				case 'N': return [k,'None','No impact',entry];
				default: return [k,null];
			}
		case 'E':
			k = 'Exploit Code Maturity';
			switch (v) {
				case 'U': return [k,'UNPROVEN','Not proved yet',entry];
				case 'P': return [k,'PROOF','Proof of concept',entry];
				case 'F': return [k,'FUNCT','Functionsl',entry];
				case 'H': return [k,'HIGH',null,entry];
				default: return [k,null];
			}
		case 'RC':
			k = 'Report Confidence';
			switch (v) {
				case 'C': return [k,'CONF','Confirmed',entry];
				case 'R': return [k,'REAS','Reasonable',entry];
				case 'U': return [k,'UNK','Unknown',entry];
				default: return [k,null];
			}
		case 'RL':
			k = 'Remediation Level';
			switch (v) {
				case 'O': return [k,'OFF-FIX','Official Fix',entry];
				case 'T': return [k,'TMP-FIX','Temporary fix',entry];
				case 'W': return [k,'WORKAROUND','Not an actual fix',entry];
				case 'U': return [k,'UNAV','Unavailable',entry];
				default: return [k,null];
			}
		case 'Au':
			k = 'Authentication';
			switch (v) {
				case 'M': return [k,'MULTI','Multiple authentication',entry];
				case 'S': return [k,'SINGLE','Single authentication',entry];
				case 'N': return [k,'None','No authentication required',entry];
				default: return [k,null];
			}
		default: return [k,null];
	}
}

const vector = ( vs ) => {
	if ( ! vs ) return null;
	return vs.split('/')
		.map( xpose )
		.filter( x => typeof x[1] === 'string')
		.sort( (a,b) => a[0].localeCompare(b[0]))
		.map( ([k,v,d,e],idx) => (
			<tr className={"vector"} key={idx}>
				<td>{k}</td>
				<td>{v}</td>
				<td>{e}</td>
				<td>{d ?? ''}</td>
			</tr>
		));
}

const MetricInfoModal = class extends Component{
	constructor(props) {
		super(props);
		this.state = {
			ll: Glob.getLanguageLong(),
			source: (props.metric.version||'').toLowerCase().includes("nvd") ? "nvd" : "cve.org"
		};
		this.load = this.load.bind(this);
		this.customRender = this.customRender.bind(this);
		this.saveButton = this.saveButton.bind(this);
		this.close = this.close.bind(this);
	}
	componentDidMount() {
		this.load();
	}
	load( data ) {
		if ( !data ) {
			this.setState({ loading: true}, ()=>{
				appLib.api('cve/get', { id: this.props.cve }).then( x => {
					if ( x ) {
						this.setState({
							origData: Glob.clone(x),
							source: (x.dataType||'').toLowerCase().includes("nvd") ? "nvd" : "cve.org"
						}, ()=>{ this.load(x); });
					}
				}).catch(e => {
					console.error(e);
				}).finally( () => {
					this.setState({ loading: false });
				});
			});
			return;
		}
		const x = {};
		if ( this.state.source === 'nvd' ) {
			if ( Array.isArray( data.descriptions ) && data.descriptions[0] ) {
				const dn = data.descriptions.find( d => d?.lang === 'en') || data.descriptions[0];
				const d = ( (typeof dn === 'string' ? dn : dn?.value) || '').trim();
				if (d) x.desc = d;
			}
			if ( data.horus?.exploitabilityScore ) x.exploitabilityScore = data.horus?.exploitabilityScore;
			if ( data.horus?.impactScore ) x.impactScore = data.horus?.impactScore;
			if ( data.horus?.weaknesses && data.horus?.weaknesses[0] ) {
				const y = data.horus?.weaknesses.filter( w => w.match(/^cwe-[0-9]+$/i));
				if ( y.length ) x.weaknesses = y;
			}
			if (data.published) x.published = new Date(data.published);
			if (data.lastModified) x.updated = new Date(data.lastModified);
		} else if ( data?.containers && typeof data?.containers === 'object') {
			const cna = data.containers.cna || {};
			const adp = (data.containers.adp || [])[0] || {};
			const md = data.cveMetadata || {};
			x.title = ( cna.title || adp.title || '' ).trim();
			if ( Array.isArray(cna.descriptions) && cna.descriptions[0]?.value ) x.desc = cna.descriptions[0].value;
			if ( md.datePublished) x.published = new Date(md.datePublished);
			if ( md.dateUpdated) x.updated = new Date(md.dateUpdated);
			if ( md.published && md.updated && md.published.getTime() === md.updated.getTime() ) delete x.updated;
			if ( x.title && x.title === x.desc ) delete x.desc;
		}
		if ( Object.keys(x).length ) this.setState({ data: x });
	}
	customRender() {
		return null;
	}
	saveButton() {
		const ll = window.document.documentElement.getAttribute('lang') || 'en-GB';
		return (
			<Button
				disabled={ ! this.state.origData }
				className={"btn-icon"}
				color={"success"}
				onClick={ ()=>{
					Glob.saveJSON(this.state.origData, this.props.cve.toLocaleLowerCase(ll)+'.json');
				} }
			>
				<b>
					<TextLabel value={"Download"} />
				</b>
				<i>
					<AppDownloadIcon />
				</i>
			</Button>
		);
	}
	close() {
		if ( typeof this.props.close === 'function' ) this.props.close();
	}
	render() {
		const m = this.props.metric || {};
		const ll = window.document.documentElement.getAttribute('lang') || 'en-GB';
		return (
			<Modal
				className={prefix+( this.props.className ? ' '+this.props.className : '' ) }
				isOpen={true}
				fade={false}
				size={'lg'}
				toggle={this.close}
			>
				<ModalHeader toggle={this.close}>
					<AppCpeCveIcon />
					{ this.props.cve.toLocaleUpperCase(ll) }
				</ModalHeader>
				<ModalBody className={ this.state.loading ? 'loading' : 'loaded' }>
					{ this.customRender() }
					<div className={"info"}>
						<a
							href={
								(this.state.source === "nvd" ?"https://nvd.nist.gov/vuln/detail/":"https://www.cve.org/CVERecord?id=")
								+this.props.cve.toLocaleUpperCase(ll)
							}
							target="_blank"
							rel="noopener noreferrer"
							title={(this.state.source === "nvd" ? "nvd.nist.gov" : "www.cve.org")+': '+this.props.cve.toLocaleUpperCase(ll)}
						>
							<AppExternalLinkIcon />
							<span>{this.state.source === "nvd" ? "nvd.nist.gov" : "cve.org"}</span>
						</a>
						{ (!! m.vendor) && (
							<div>
								<em><TextLabel value={"Vendor"} /></em>
								<strong>{ m.vendor.replace(/_/g,' ') }</strong>
							</div>
						)}
						{ (!! m.product)  && (
							<div>
								<em><TextLabel value={"Model"}/></em>
								<strong>{ m.product.replace(/_/g,' ')}</strong>
							</div>
						)}
						{ (!! this.state.source) && (
							<div>
								<em><TextLabel value={"Source"}/></em>
								<span>{ this.state.source.toUpperCase() }</span>
							</div>
						)}
						{ (!! this.state.data?.published) && (
							<div>
								<em><TextLabel value={"Published"}/></em>
								<span>{ this.state.data?.published.toLocaleString( this.state.ll ) }</span>
							</div>
						)}
						{ (!! this.state.data?.updated) && (
							<div>
								<em><TextLabel value={"Updated"}/></em>
								<span>{ this.state.data?.updated.toLocaleString( this.state.ll ) }</span>
							</div>
						)}
						{ (!! this.state.data?.weaknesses) && (
							<div>
								<em><TextLabel value={"Weaknesses"}/></em>
								<div>
									{ this.state.data?.weaknesses.map( (c,idx) => {
										let out = [];
										if ( idx > 0 ) out.push(<span key={idx}>,</span>);
										if ( c.match(/^CWE-[0-9]+$/)) {
											const x = parseInt( c.replace(/^CWE-([0-9]+)$/,"$1") );
											out.push(<a key={c} href={`https://cwe.mitre.org/data/definitions/${x}.html`} target="_blank" rel="noopener noreferrer">{c}</a>);
										} else {
											out.push(<span key={c}>c</span>);
										}
										return out;
									}) }
								</div>
							</div>
						) }
						{ (!! (this.state.data?.title || this.state.data?.desc )) && (
							<div>
								<em><TextLabel value={"Details"}/></em>
								<div>
									{ !! this.state.data?.title && <strong>{ this.state.data?.title }</strong> }
									{ !! this.state.data?.desc && <div>{ this.state.data?.desc }</div> }
								</div>
							</div>
						)}
					</div>
					<div className={"vector"}>
						<table>
							<tbody>
							<tr className={"base"}>
								<td>
									<TextLabel value={"Base score"} />
								</td>
								<td>
									{ m.baseScore }
								</td>
								<td className={ m.baseSeverity || '' }>
									{ m.baseSeverity || '' }
								</td>
								<td>
									<TextLabel value={"Base severity"} />
								</td>
							</tr>
							{ ( !! (this.state.data?.exploitabilityScore||this.state.data?.impactScore) ) && (
								<tr className={"base"}>
									<td>
										<TextLabel value={"Impact score"} />
									</td>
									<td>
										{ this.state.data.impactScore || '—'}
									</td>
									<td>
										{ this.state.data.exploitabilityScore || '' }
									</td>
									<td>
										<TextLabel value={"Exploitability score"} />
									</td>
								</tr>
							)}
							{ vector(this.props.metric.vectorString) }
							</tbody>
						</table>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						className={"btn-icon"}
						color={"warning"}
						onClick={ this.close }
					>
						<i>
							<CloseIcon />
						</i>
						<b>
							<TextLabel value={"Cancel"} />
						</b>
					</Button>
					{ this.saveButton() }
				</ModalFooter>
			</Modal>
		)
	}
}

export default MetricInfoModal;